import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import "./calculadora.css";

const parseFloatSafe = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) => Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CANALES = [
  { key: "BTRAVEL", label: "BTRAVEL", comisionDefault: 20 },
  { key: "GRUPOS",  label: "GRUPOS",  comisionDefault: 20 },
  { key: "DIRECTO", label: "DIRECTO", comisionDefault: 15 },
];
const PAGOS = [
  { key: "SPEI",   label: "SPEI" },
  { key: "TDC1X",  label: "1x TDC" },
  { key: "MSI3",   label: "3 MSI" },
  { key: "MSI6",   label: "6 MSI" },
];

export default function TraveldeskCalculadora() {
  // TC de sistema (mismo que barra superior — banxico + incremento vigente)
  const [tcSistema, setTcSistema] = useState({ usd: null, eur: null, usd_fecha: "", eur_fecha: "" });

  // Estado UI
  const [gastoEnUSD, setGastoEnUSD] = useState(false);
  const [tcProveedor, setTcProveedor] = useState("");
  const [base, setBase] = useState("");
  const [tax, setTax] = useState("");
  const [comision, setComision] = useState(20);
  const [autorizacionComision, setAutorizacionComision] = useState(false);
  const [canalVenta, setCanalVenta] = useState("BTRAVEL");
  const [formaPago, setFormaPago] = useState("SPEI");

  useEffect(() => {
    const fetchTC = async () => {
      try {
        const r = await axios.get(`${API}/tipo-cambio`);
        setTcSistema(r.data || {});
      } catch (e) {
        toast.error("No se pudo cargar TC");
      }
    };
    fetchTC();
    const onTcUpdated = () => fetchTC();
    window.addEventListener("tc-updated", onTcUpdated);
    return () => window.removeEventListener("tc-updated", onTcUpdated);
  }, []);

  // Cambio automático del % según canal a menos que se autorice manual
  useEffect(() => {
    if (autorizacionComision) return;
    const c = CANALES.find((x) => x.key === canalVenta);
    if (c) setComision(c.comisionDefault);
  }, [canalVenta, autorizacionComision]);

  // Total gasto
  const totalGastoUSD = useMemo(() => {
    if (!gastoEnUSD) return 0;
    return parseFloatSafe(base) + parseFloatSafe(tax);
  }, [gastoEnUSD, base, tax]);

  const totalGastoMXN = useMemo(() => {
    if (gastoEnUSD) {
      return (parseFloatSafe(base) + parseFloatSafe(tax)) * parseFloatSafe(tcProveedor);
    }
    return parseFloatSafe(base) + parseFloatSafe(tax);
  }, [gastoEnUSD, base, tax, tcProveedor]);

  // Cálculo PVP (siguiendo lógica original RTF)
  const calcVentaMXN = (extraMargen) => {
    const u = parseFloatSafe(comision) + extraMargen;
    const d = 1 - (u / 100);
    if (d <= 0) return 0;
    const b = parseFloatSafe(base);
    const t = parseFloatSafe(tax);
    if (gastoEnUSD) {
      const tarifaBaseUSD = b / d;
      const totalPVPusd = tarifaBaseUSD + t;
      const tcUsd = parseFloatSafe(tcSistema.usd);
      return totalPVPusd * tcUsd;
    }
    // Modo MXN: base y tax ya vienen en MXN
    const tarifaBaseMXN = b / d;
    return tarifaBaseMXN + t;
  };

  const calcVentaUSD = (extraMargen) => {
    const u = parseFloatSafe(comision) + extraMargen;
    const d = 1 - (u / 100);
    if (d <= 0) return 0;
    const b = parseFloatSafe(base);
    const t = parseFloatSafe(tax);
    if (gastoEnUSD) {
      return (b / d) + t;
    }
    // Convertir desde MXN a USD dividiendo por TC
    const tcUsd = parseFloatSafe(tcSistema.usd) || 1;
    return ((b / d) + t) / tcUsd;
  };

  const pvpMXN = calcVentaMXN(0);

  // Total a pagar según forma de pago (Openpay BBVA MX: tasa + $3 + IVA sobre la comisión)
  const totalPago = useMemo(() => {
    const base = pvpMXN;
    switch (formaPago) {
      case "SPEI":  return base;
      case "TDC1X": return base + (base * 0.034 + 3) * 1.16;
      case "MSI3":  return base + (base * 0.049 + 3) * 1.16;
      case "MSI6":  return base + (base * 0.079 + 3) * 1.16;
      default:      return base;
    }
  }, [pvpMXN, formaPago]);

  const resetFields = () => {
    setGastoEnUSD(true);
    setTcProveedor("");
    setBase("");
    setTax("");
    setComision(20);
    setAutorizacionComision(false);
    setCanalVenta("BTRAVEL");
    setFormaPago("SPEI");
  };

  return (
    <div className="calc-page" data-testid="calculadora-tarifas">
      <div className="calc-wrapper">
        {/* Header */}
        <div className="calc-header">
          <div className="calc-title">Calculadora de Tarifas</div>
          <div className="calc-sub">Precio de venta público (PVP) por canal, gasto y utilidad</div>
        </div>

        {/* TC strip */}
        <div className="tc-strip">
          <div className="tc-item">
            <div className="tc-label">TC USD</div>
            <div className="tc-value" data-testid="tc-usd">${fmt(tcSistema.usd)}</div>
            <div className="tc-date">{tcSistema.usd_fecha || "—"}</div>
          </div>
          <div className="tc-item">
            <div className="tc-label">TC EUR</div>
            <div className="tc-value" data-testid="tc-eur">${fmt(tcSistema.eur)}</div>
            <div className="tc-date">{tcSistema.eur_fecha || "—"}</div>
          </div>
        </div>

        {/* Canal */}
        <div className="calc-card">
          <div className="calc-card-title">Canal de venta</div>
          <div className="canal-group">
            {CANALES.map((c) => (
              <button key={c.key}
                className={`canal-btn ${canalVenta === c.key ? "active " + c.key.toLowerCase() : ""}`}
                onClick={() => setCanalVenta(c.key)}
                data-testid={`canal-${c.key}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gasto */}
        <div className="calc-card">
          <div className="calc-card-title">Gasto</div>
          <div className="toggle-row">
            <label className="toggle">
              <input type="checkbox" checked={gastoEnUSD} onChange={(e) => setGastoEnUSD(e.target.checked)} data-testid="chk-gasto-usd" />
              <span className="toggle-track" />
            </label>
            <span>Gasto en USD? <span className="mut">(desmarcado = MXN)</span></span>
          </div>
          <div className="form-grid">
            <div className="field">
              <label>TC Proveedor ($)</label>
              <input type="text" inputMode="decimal"
                value={tcProveedor}
                onChange={(e) => setTcProveedor(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                disabled={!gastoEnUSD}
                placeholder={gastoEnUSD ? "Ej. 18.5000" : "—"}
                data-testid="inp-tc-proveedor" />
            </div>
            <div className="field">
              <label>Base ($)</label>
              <input type="text" inputMode="decimal"
                value={base}
                onChange={(e) => setBase(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                data-testid="inp-base" />
            </div>
            <div className="field">
              <label>Tax ($)</label>
              <input type="text" inputMode="decimal"
                value={tax}
                onChange={(e) => setTax(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                data-testid="inp-tax" />
            </div>
          </div>
          <div className="totals-row">
            <div className="total-chip">Total gasto USD <b>${fmt(totalGastoUSD)}</b></div>
            <div className="total-chip">Total gasto MXN <b>${fmt(totalGastoMXN)}</b></div>
          </div>
        </div>

        {/* Utilidad */}
        <div className="calc-card">
          <div className="calc-card-title">Utilidad</div>
          <div className="util-row">
            <div className="util-input-wrap">
              <label>Comisión (%)</label>
              <div className="input-with-pct">
                <input type="number" min="0" max="99" step="0.5"
                  value={comision}
                  onChange={(e) => setComision(Number(e.target.value))}
                  disabled={!autorizacionComision}
                  data-testid="inp-comision" />
                <span className="pct-sign">%</span>
              </div>
            </div>
            <label className="auth-check">
              <input type="checkbox" checked={autorizacionComision}
                onChange={(e) => setAutorizacionComision(e.target.checked)}
                data-testid="chk-autoriza" />
              <span>Autorizado a modificar % de utilidad</span>
            </label>
          </div>
        </div>

        {/* PVP HERO */}
        <div className={`pvp-hero canal-${canalVenta.toLowerCase()}`} data-testid="pvp-hero">
          <div className="pvp-label">Precio Venta Público (PVP)</div>
          <div className="pvp-value">${fmt(pvpMXN)} <span className="pvp-cur">MXN</span></div>
          <div className="pvp-meta">Comisión {parseFloatSafe(comision)}% · Canal {canalVenta}</div>
        </div>

        {/* Tabla referencia */}
        <div className="calc-card">
          <div className="calc-card-title">Tabla de referencia</div>
          <table className="ref-table">
            <thead>
              <tr>
                <th>Utilidad</th>
                <th className="r">PVP (USD)</th>
                <th className="r">PVP (MXN)</th>
              </tr>
            </thead>
            <tbody>
              {[0, 5, 10].map((e) => {
                const u = parseFloatSafe(comision) + e;
                return (
                  <tr key={e} className={e === 0 ? "row-base" : ""}>
                    <td><b>{u}%</b> {e > 0 && <span className="mut">(+{e}%)</span>}</td>
                    <td className="r mono">${fmt(calcVentaUSD(e))}</td>
                    <td className="r mono">${fmt(calcVentaMXN(e))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pago */}
        <div className="calc-card">
          <div className="calc-card-title">Forma de pago</div>
          <div className="pago-group">
            {PAGOS.map((p) => (
              <button key={p.key}
                className={`pago-btn ${formaPago === p.key ? "active" : ""}`}
                onClick={() => setFormaPago(p.key)}
                data-testid={`pago-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="total-final-wrap">
            <span className="total-final-label">Total a pagar</span>
            <span className="total-final-amount" data-testid="total-pago">${fmt(totalPago)} <span className="pvp-cur">MXN</span></span>
          </div>
        </div>

        <button className="reset-btn" onClick={resetFields} data-testid="btn-reset">
          <RotateCcw size={14} /> Limpiar
        </button>

        <div className="calc-footer">© pd travel · Calculadora nativa · TC: BANXICO + incremento configurado</div>
      </div>
    </div>
  );
}
